import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChartColumn } from 'lucide-react'
import { salesReportQuery, type CashierTakings, type SalesReport } from '@/api/goods'
import { branchesQuery } from '@/api/queries'
import { GoodsTabs } from '@/app/goods/goods-shell'
import { ShelfFilter } from '@/app/goods/shelf'
import { useGoodsActions } from '@/app/goods/use-goods-actions'
import { useShelfFilter } from '@/app/goods/use-shelf'
import { usePaperNames } from '@/app/goods/use-paper-names'
import { NothingForRole } from '@/app/nothing-for-role'
import { visibleBranches } from '@/domain/client-abilities'
import { errorText } from '@/domain/errors'
import { goodsErrorText } from '@/domain/goods-errors'
import {
  costKnown,
  figureText,
  marginShare,
  PERIODS,
  periodRange,
  periodText,
  reportZone,
  unitsSold,
  type Period,
} from '@/domain/goods'
import { clubSearch, shelfFromKey, shelfKey } from '@/domain/shelves'
import { formatMoney, formatMoneyColumn } from '@/domain/pass-rules'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { withKey } from '@/lib/search'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'
import { StateFilter } from '@/ui/state-filter'

/// `club` — the shelf the report is cut by (domain/shelves.ts `shelfKey`),
/// in the URL with the period, so a reload, a link and «Назад» keep both.
type ReportSearch = { period?: Period; club?: string }

export const Route = createFileRoute('/_app/c/$orgId/goods/sales')({
  validateSearch: (raw: Record<string, unknown>): ReportSearch => ({
    ...(typeof raw['period'] === 'string' && (PERIODS as readonly string[]).includes(raw['period']) && raw['period'] !== 'week'
      ? { period: raw['period'] as Period }
      : {}),
    ...clubSearch(raw['club']),
  }),
  component: Sales,
})

const r = () => t.goods.report

type Row = SalesReport['products'][number]

/// «Продажі»: what the goods earned over a period — revenue at the prices
/// they sold at, cost at what they cost then, the margin, how it was paid;
/// and the same per product, best sellers first.
function Sales() {
  const { orgId } = Route.useParams()
  const { period = 'week', club } = Route.useSearch()
  const navigate = Route.useNavigate()
  const shelf = shelfFromKey(club)
  const goods = useGoodsActions(orgId, { shelf })
  const { can } = goods
  const branches = useQuery({ ...branchesQuery(orgId), enabled: can.see })
  const shelves = useShelfFilter(orgId, goods.access, can.report)
  // Days on the CLUB's calendar — the chosen club's zone, else the first
  // one's — and their boundaries, not «now»: the query key holds still all
  // day. Asked once the clubs are known, so a club in another zone is not
  // first asked about Kyiv's day.
  const span = periodRange(period, reportZone(visibleBranches(goods.access, branches.data ?? []), shelf))
  const names = usePaperNames(orgId, can.report)
  const report = useQuery({
    ...salesReportQuery(orgId, span.from, span.to, shelf),
    enabled: can.see && can.report && !branches.isPending,
    // Another period or club keeps the figures on screen, dimmed, until
    // the new ones arrive — not a spinner on every switch.
    placeholderData: keepPreviousData,
  })

  if (!can.see) return <NothingForRole />

  const columns: readonly DataColumn<Row>[] = [
    {
      key: 'name',
      header: r().columns.name,
      width: 'minmax(0,2fr)',
      cell: (row) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-text">{row.name}</span>
          <span className="truncate text-caption text-text-2">
            {costKnown(row) === 'part' ? `${row.category} · ${r().uncostedShort(row.uncostedLines)}` : row.category}
          </span>
        </span>
      ),
    },
    // On a phone: the name, «12 шт · маржа 140,00 ₴» under it, the revenue
    // on the right — the margin is what the page is about, it is not hidden.
    { key: 'qty', header: r().columns.qty, width: 'minmax(90px,0.7fr)', numeric: true, mobile: 'subtitle', cell: (row) => `${row.qty} ${row.unit}` },
    { key: 'revenue', header: r().columns.revenue, width: 'minmax(100px,0.9fr)', numeric: true, mobile: 'trailing', cell: (row) => formatMoneyColumn(row.revenueMinor) },
    // What discounts under the list gave away (S12) — a desktop column.
    {
      key: 'discount',
      header: r().columns.discount,
      width: 'minmax(90px,0.7fr)',
      numeric: true,
      hideBelow: 'xl',
      mobile: 'hidden',
      cell: (row) => <span className={row.discountMinor > 0 ? undefined : 'text-muted'}>{formatMoneyColumn(row.discountMinor)}</span>,
    },
    // «—» when nothing of the product was costed, «≈» when part of it is an
    // estimate (specs/shop.md S13) — never «0 ₴».
    {
      key: 'cost',
      header: r().columns.cost,
      width: 'minmax(100px,0.9fr)',
      numeric: true,
      hideBelow: 'md',
      mobile: 'hidden',
      cell: (row) => figureText(row.costMinor, row, formatMoneyColumn),
    },
    {
      key: 'margin',
      header: r().columns.margin,
      width: 'minmax(100px,0.9fr)',
      numeric: true,
      mobile: 'subtitle',
      cell: (row) => (
        <span className={row.marginMinor < 0 && costKnown(row) !== 'none' ? 'text-danger' : undefined}>
          <span className="sm:hidden">{r().marginShort} </span>
          {figureText(row.marginMinor, row, formatMoneyColumn)}
        </span>
      ),
    },
    {
      key: 'share',
      header: r().columns.share,
      width: 'minmax(80px,0.6fr)',
      numeric: true,
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (row) => {
        const pct = marginShare(row)
        return pct == null ? '—' : `${pct} %`
      },
    },
  ]

  // Who took the goods' money — what a drawer is reconciled against while
  // the goods' cash is not in «Каса» (E6).
  const cashierColumns: readonly DataColumn<CashierTakings>[] = [
    { key: 'who', header: r().cashierColumns.who, width: 'minmax(0,2fr)', cell: (row) => names.author(row.userId) },
    { key: 'sales', header: r().cashierColumns.sales, width: 'minmax(80px,0.6fr)', numeric: true, mobile: 'subtitle', cell: (row) => String(row.sales) },
    { key: 'cash', header: r().cashierColumns.cash, width: 'minmax(100px,0.9fr)', numeric: true, mobile: 'trailing', cell: (row) => formatMoneyColumn(row.cashMinor) },
    { key: 'card', header: r().cashierColumns.card, width: 'minmax(100px,0.9fr)', numeric: true, mobile: 'subtitle', cell: (row) => formatMoneyColumn(row.cardMinor) },
  ]

  const totals = report.data?.totals
  const pct = totals ? marginShare(totals) : null

  return (
    <Page wide>
      <PageHeader title={t.goods.title} subtitle={t.goods.subtitle} actions={goods.actionsFor()} tabs={<GoodsTabs orgId={orgId} can={can} />} />
      {goods.news ? <InlineBanner kind="done" className="mb-6" message={goods.news} /> : null}

      {can.report ? (
        <div className="mb-5 flex flex-col gap-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <StateFilter
              label={r().period}
              value={period}
              onChange={(next) => void navigate({ search: (prev) => withKey(prev, 'period', next === 'week' ? undefined : next), replace: true })}
              options={PERIODS.map((p) => ({ value: p, label: r().periods[p] ?? p, count: undefined }))}
            />
            <ShelfFilter
              filter={shelves}
              value={shelf}
              onChange={(next) =>
                void navigate({ search: (prev) => withKey(prev, 'club', next.kind === 'all' ? undefined : shelfKey(next)), replace: true })
              }
            />
          </div>
          {/* What «7 днів» is today, on the club's calendar. */}
          <p className="px-1 text-caption text-text-2 tabular">{periodText(span.firstDay, span.lastDay)}</p>
        </div>
      ) : null}

      {!can.report ? (
        <InlineBanner kind="empty" message={r().noAccess} />
      ) : report.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : report.isError ? (
        <InlineBanner kind="error" message={errorText(report.error, goodsErrorText)} action={{ label: t.app.retry, onClick: () => void report.refetch() }} />
      ) : !totals || totals.sales === 0 ? (
        <EmptyState icon={<ChartColumn />} title={r().empty} message={r().emptyMessage} />
      ) : (
        <div className={cn('flex flex-col gap-6 transition-opacity', report.isPlaceholderData && 'opacity-60')}>
          {/* The section's one tile (ui/stat-tile.tsx), not a fifth copy:
              two across on a phone, four on a desktop. */}
          <StatTiles className="grid-cols-2 lg:grid-cols-4">
            <StatTile
              compact
              label={r().revenue}
              value={formatMoney(totals.revenueMinor)}
              note={
                <>
                  {r().cashCard(formatMoney(totals.cashMinor), formatMoney(totals.cardMinor))}
                  {totals.discountMinor > 0 ? (
                    <>
                      <br />
                      {r().discounts(formatMoney(totals.discountMinor))}
                    </>
                  ) : null}
                </>
              }
            />
            <StatTile compact label={r().cost} value={figureText(totals.costMinor, totals, formatMoney)} note={r().costMethod} />
            {/* Neutral: a margin is a figure to read, not a pass mark — and
                with lines left out of it, green would be a claim. Red only
                when it is below zero. */}
            <StatTile
              compact
              label={r().margin}
              value={figureText(totals.marginMinor, totals, formatMoney)}
              note={pct == null ? undefined : r().marginShare(pct)}
              tone={totals.marginMinor < 0 && costKnown(totals) !== 'none' ? 'danger' : 'normal'}
            />
            <StatTile compact label={r().sales} value={String(totals.sales)} note={unitsSold(report.data?.products ?? [])} />
          </StatTiles>
          {totals.uncostedLines > 0 ? <InlineBanner kind="stale" message={r().uncosted(totals.uncostedLines)} /> : null}
          {totals.estimatedLines > 0 ? <InlineBanner kind="stale" message={r().estimated(totals.estimatedLines)} /> : null}
          <section className="flex flex-col gap-2">
            <SectionHeader as="h2" title={r().byProduct} inset />
            <DataList
              label={r().byProduct}
              columns={columns}
              rows={report.data?.products ?? []}
              rowKey={(row) => row.productId}
              rowTarget={(row) => ({ to: '/c/$orgId/goods/$productId', params: { orgId, productId: row.productId } })}
              empty={r().empty}
            />
            <p className="px-4 text-caption text-muted">{r().costFootnote}</p>
          </section>
          <section className="flex flex-col gap-2">
            <SectionHeader as="h2" title={r().byCashier} inset />
            <DataList
              label={r().byCashier}
              columns={cashierColumns}
              rows={report.data?.cashiers ?? []}
              rowKey={(row) => row.userId ?? 'nobody'}
              empty={r().empty}
            />
            <p className="px-4 text-caption text-muted">{r().cashierNote}</p>
          </section>
        </div>
      )}

      {goods.panel}
    </Page>
  )
}
